import React, { useState, useEffect } from 'react';
import { Phone, Clock, Calendar, Filter, Download, Search, PhoneIncoming, PhoneOutgoing, PhoneMissed } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI } from '../../services/api';

const ClientReports = () => {
  const { clientUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    avgDuration: '0:00'
  });
  const [filters, setFilters] = useState({
    dateRange: 'all',
    agent: 'all',
    status: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReportData();
  }, [clientUser]);

  const fetchReportData = async () => {
    try {
      // Fetch agents
      const agentsRes = await reportsAPI.getAgents(clientUser?.id);
      setAgents(agentsRes.data.agents || []);

      // Fetch call logs
      const callsRes = await reportsAPI.getCalls(clientUser?.id);
      setCalls(callsRes.data.calls || []);

      // Calculate stats
      const allCalls = callsRes.data.calls || [];
      const now = new Date();
      const today = allCalls.filter(c => {
        const callDate = new Date(c.created_at);
        return callDate.toDateString() === now.toDateString();
      }).length;

      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thisWeek = allCalls.filter(c => new Date(c.created_at) >= weekAgo).length;

      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const thisMonth = allCalls.filter(c => new Date(c.created_at) >= monthStart).length;

      setStats({
        today,
        thisWeek,
        thisMonth,
        avgDuration: '0:00'
      });
    } catch (err) {
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCalls = calls.filter(call => {
    if (filters.agent !== 'all' && call.agent_id !== filters.agent) return false;
    if (filters.status !== 'all' && call.status !== filters.status) return false;
    if (searchTerm && !call.caller_id?.includes(searchTerm)) return false;
    return true;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <PhoneIncoming className="w-4 h-4 text-green-400" />;
      case 'outgoing': return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
      case 'missed': return <PhoneMissed className="w-4 h-4 text-red-400" />;
      default: return <Phone className="w-4 h-4 text-gray-400" />;
    }
  };

  const statCards = [
    { label: 'Today', value: stats.today, icon: Calendar, color: 'text-blue-400' },
    { label: 'This Week', value: stats.thisWeek, icon: Calendar, color: 'text-green-400' },
    { label: 'This Month', value: stats.thisMonth, icon: Calendar, color: 'text-purple-400' },
    { label: 'Avg Duration', value: stats.avgDuration, icon: Clock, color: 'text-teal-400' },
  ];

  return (
    <div className="p-8" data-testid="client-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Call Reports</h1>
          <p className="text-gray-400">View call history and analytics</p>
        </div>
        <button className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors">
          <Download className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-[#0d1117] border border-gray-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{stat.label}</span>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <p className="text-2xl font-bold text-white">{loading ? '-' : stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search by caller ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            />
          </div>
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <select
            value={filters.agent}
            onChange={(e) => setFilters({...filters, agent: e.target.value})}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
          >
            <option value="all">All Agents</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>{agent.name}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
            <option value="outgoing">Outgoing</option>
          </select>
        </div>
      </div>

      {/* Call History Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        {filteredCalls.length === 0 ? (
          <div className="text-center py-16">
            <Phone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No calls yet</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              When your voice agents start receiving calls, they will appear here with detailed information.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1a1a1f]">
                <tr>
                  <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">STATUS</th>
                  <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">CALLER ID</th>
                  <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">AGENT</th>
                  <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">DURATION</th>
                  <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">DATE & TIME</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalls.map((call) => (
                  <tr key={call.id} className="border-t border-gray-800 hover:bg-[#1a1a1f]/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(call.status)}
                        <span className={`text-sm capitalize ${
                          call.status === 'completed' ? 'text-green-400' :
                          call.status === 'missed' ? 'text-red-400' :
                          'text-blue-400'
                        }`}>
                          {call.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white font-mono">{call.caller_id}</td>
                    <td className="px-6 py-4 text-gray-300">{call.agent_name}</td>
                    <td className="px-6 py-4 text-gray-300">{call.duration || '-'}</td>
                    <td className="px-6 py-4 text-gray-400">
                      {new Date(call.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientReports;
